import React from "react";
import { Badge, Button, Callout, Card, DataTable, Dialog, Field, Input, PageHeader, Select, Tabs } from "../components/DesignSystem";
import type { PhotographyOverview } from "../domain/types";
import { errorMessage } from "../services/errors";
import { usePlannerData } from "../state/PlannerDataContext";
import { ErrorState, LoadingState, useSubmitGuard } from "./common";

export function PhotographyScreen() {
  const { workspace, loading, error, service } = usePlannerData();
  const [tab, setTab] = React.useState<"photographer" | "groups" | "shots">("photographer");
  const [overview, setOverview] = React.useState<PhotographyOverview | null>(null);
  const [overviewLoading, setOverviewLoading] = React.useState(false);
  const [overviewError, setOverviewError] = React.useState<string | null>(null);
  const [addingEngagement, setAddingEngagement] = React.useState(false);
  const [addingGroup, setAddingGroup] = React.useState(false);
  const [addingShot, setAddingShot] = React.useState(false);
  const [actionError, setActionError] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    setOverviewLoading(true);
    setOverviewError(null);
    try {
      setOverview(await service.photography.getOverview());
    } catch (err) {
      setOverviewError(errorMessage(err));
    } finally {
      setOverviewLoading(false);
    }
  }, [service]);

  React.useEffect(() => { void load(); }, [load]);

  if (loading || !workspace) return <div className="pob-pad"><LoadingState label="Loading photography" /></div>;
  if (error) return <div className="pob-pad"><ErrorState message={error} /></div>;

  return (
    <div>
      <PageHeader
        eyebrow="More"
        title="Photography"
        tabs={<Tabs value={tab} onChange={setTab} tabs={[{ value: "photographer", label: "Photographer" }, { value: "groups", label: "Family groups" }, { value: "shots", label: "Shot list" }]} />}
        actions={tab === "photographer" ? <Button variant="primary" onClick={() => setAddingEngagement(true)}>Add photographer</Button> : tab === "groups" ? <Button variant="primary" onClick={() => setAddingGroup(true)}>Add group</Button> : <Button variant="primary" onClick={() => setAddingShot(true)}>Add shot</Button>}
      />
      <div className="pob-pad">
        {overviewLoading && !overview && <LoadingState label="Loading photography" />}
        {overviewError && <ErrorState message={overviewError} retry={load} />}
        {actionError && <Callout tone="error" title="That didn't work" style={{ marginBottom: "var(--space-4)" }}>{actionError}</Callout>}

        {overview && tab === "photographer" && (
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
            {overview.engagements.length === 0 && <span style={{ font: "var(--type-body-sm)", color: "var(--text-secondary)" }}>No photographer linked yet.</span>}
            {overview.engagements.map((e) => (
              <Card key={e.id} eyebrow={e.packageName || "Package"} title={e.vendorName} action={<Button size="sm" variant="danger" onClick={async () => { try { await service.photography.deleteEngagement(e.id); await load(); } catch (err) { setActionError(errorMessage(err)); } }}>Remove</Button>}>
                {e.deliverables && <p style={{ font: "var(--type-body-sm)" }}>{e.deliverables}</p>}
                <div style={{ display: "flex", gap: "var(--space-2)", flexWrap: "wrap", marginTop: "var(--space-2)" }}>
                  {e.coverageEvents.map((ev) => <Badge key={ev.id} tone="neutral">{ev.name}</Badge>)}
                </div>
              </Card>
            ))}
          </div>
        )}

        {overview && tab === "groups" && (
          <Card padding="0">
            <DataTable
              rows={overview.photoGroups}
              emptyLabel="No photo groups yet"
              columns={[
                { key: "name", header: "Group" },
                { key: "eventId", header: "Celebration", render: (row) => workspace.events.find((ev) => ev.id === row.eventId)?.name || row.eventId },
                { key: "headcount", header: "Headcount", width: 90, muted: true },
                { key: "time", header: "Time", width: 80, muted: true },
                { key: "actions", header: "", width: 90, render: (row) => <Button size="sm" variant="danger" onClick={async () => { try { await service.photography.deletePhotoGroup(row.id); await load(); } catch (err) { setActionError(errorMessage(err)); } }}>Remove</Button> }
              ]}
            />
          </Card>
        )}

        {overview && tab === "shots" && (
          <Card padding="0">
            <DataTable
              rows={overview.shotList}
              emptyLabel="No shots planned yet"
              columns={[
                { key: "item", header: "Shot" },
                { key: "eventId", header: "Celebration", render: (row) => workspace.events.find((ev) => ev.id === row.eventId)?.name || row.eventId },
                { key: "priority", header: "Priority", width: 90, render: (row) => <Badge tone={row.priority === "must" ? "error" : "neutral"}>{row.priority}</Badge> },
                { key: "time", header: "Time", width: 80, muted: true },
                { key: "actions", header: "", width: 90, render: (row) => <Button size="sm" variant="danger" onClick={async () => { try { await service.photography.deleteShotListItem(row.id); await load(); } catch (err) { setActionError(errorMessage(err)); } }}>Remove</Button> }
              ]}
            />
          </Card>
        )}
      </div>

      {addingEngagement && <EngagementDialog onClose={() => setAddingEngagement(false)} onSaved={async () => { setAddingEngagement(false); await load(); }} />}
      {addingGroup && <GroupDialog onClose={() => setAddingGroup(false)} onSaved={async () => { setAddingGroup(false); await load(); }} />}
      {addingShot && <ShotDialog onClose={() => setAddingShot(false)} onSaved={async () => { setAddingShot(false); await load(); }} />}
    </div>
  );
}

function EngagementDialog({ onClose, onSaved }: { onClose: () => void; onSaved: () => Promise<void> }) {
  const { service, workspace } = usePlannerData();
  const [vendorId, setVendorId] = React.useState(workspace!.vendors[0]?.id || "");
  const [packageName, setPackageName] = React.useState("");
  const [deliverables, setDeliverables] = React.useState("");
  const [formError, setFormError] = React.useState<string | null>(null);
  const { submitting, guardedSubmit } = useSubmitGuard();

  const submit = () => guardedSubmit(async () => {
    setFormError(null);
    if (!vendorId) { setFormError("Choose a vendor."); return; }
    try { await service.photography.upsertEngagement({ vendorId, packageName: packageName || undefined, deliverables: deliverables || undefined }); await onSaved(); } catch (err) { setFormError(errorMessage(err)); }
  });

  return (
    <Dialog title="Add photographer" onClose={onClose} footer={<><Button size="sm" onClick={onClose} disabled={submitting}>Cancel</Button><Button size="sm" variant="primary" onClick={submit} disabled={submitting}>{submitting ? "Saving…" : "Save"}</Button></>}>
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
        {formError && <Callout tone="error" title="Check the form">{formError}</Callout>}
        <Field label="Vendor"><Select value={vendorId} onChange={(e) => setVendorId(e.target.value)} options={workspace!.vendors.map((v) => ({ value: v.id, label: v.name }))} /></Field>
        <Field label="Package"><Input value={packageName} onChange={(e) => setPackageName(e.target.value)} placeholder="Full day coverage" /></Field>
        <Field label="Deliverables"><Input value={deliverables} onChange={(e) => setDeliverables(e.target.value)} placeholder="500 edited photos, album" /></Field>
      </div>
    </Dialog>
  );
}

function GroupDialog({ onClose, onSaved }: { onClose: () => void; onSaved: () => Promise<void> }) {
  const { service, workspace } = usePlannerData();
  const [eventId, setEventId] = React.useState(workspace!.events[0]?.id || "");
  const [name, setName] = React.useState("");
  const [headcount, setHeadcount] = React.useState("");
  const [time, setTime] = React.useState("");
  const [formError, setFormError] = React.useState<string | null>(null);
  const { submitting, guardedSubmit } = useSubmitGuard();

  const submit = () => guardedSubmit(async () => {
    setFormError(null);
    if (!eventId || !name.trim()) { setFormError("Choose a celebration and name the group."); return; }
    try { await service.photography.createPhotoGroup({ eventId, name: name.trim(), headcount: headcount ? Number(headcount) : undefined, time: time || undefined }); await onSaved(); } catch (err) { setFormError(errorMessage(err)); }
  });

  return (
    <Dialog title="Add photo group" onClose={onClose} footer={<><Button size="sm" onClick={onClose} disabled={submitting}>Cancel</Button><Button size="sm" variant="primary" onClick={submit} disabled={submitting}>{submitting ? "Adding…" : "Add"}</Button></>}>
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
        {formError && <Callout tone="error" title="Check the form">{formError}</Callout>}
        <Field label="Celebration"><Select value={eventId} onChange={(e) => setEventId(e.target.value)} options={workspace!.events.map((ev) => ({ value: ev.id, label: ev.name }))} /></Field>
        <Field label="Group name"><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Immediate family" /></Field>
        <Field label="Headcount"><Input value={headcount} type="number" onChange={(e) => setHeadcount(e.target.value)} /></Field>
        <Field label="Time"><Input type="time" value={time} onChange={(e) => setTime(e.target.value)} /></Field>
      </div>
    </Dialog>
  );
}

function ShotDialog({ onClose, onSaved }: { onClose: () => void; onSaved: () => Promise<void> }) {
  const { service, workspace } = usePlannerData();
  const [eventId, setEventId] = React.useState(workspace!.events[0]?.id || "");
  const [item, setItem] = React.useState("");
  const [priority, setPriority] = React.useState<"must" | "nice">("nice");
  const [formError, setFormError] = React.useState<string | null>(null);
  const { submitting, guardedSubmit } = useSubmitGuard();

  const submit = () => guardedSubmit(async () => {
    setFormError(null);
    if (!eventId || !item.trim()) { setFormError("Choose a celebration and describe the shot."); return; }
    try { await service.photography.createShotListItem({ eventId, item: item.trim(), priority }); await onSaved(); } catch (err) { setFormError(errorMessage(err)); }
  });

  return (
    <Dialog title="Add shot" onClose={onClose} footer={<><Button size="sm" onClick={onClose} disabled={submitting}>Cancel</Button><Button size="sm" variant="primary" onClick={submit} disabled={submitting}>{submitting ? "Adding…" : "Add"}</Button></>}>
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
        {formError && <Callout tone="error" title="Check the form">{formError}</Callout>}
        <Field label="Celebration"><Select value={eventId} onChange={(e) => setEventId(e.target.value)} options={workspace!.events.map((ev) => ({ value: ev.id, label: ev.name }))} /></Field>
        <Field label="Shot"><Input value={item} onChange={(e) => setItem(e.target.value)} placeholder="Ring exchange close-up" /></Field>
        <Field label="Priority"><Select value={priority} onChange={(e) => setPriority(e.target.value as "must" | "nice")} options={[{ value: "must", label: "Must have" }, { value: "nice", label: "Nice to have" }]} /></Field>
      </div>
    </Dialog>
  );
}
